"use client";

import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inquiryOptions = ["General Inquiry", "Balayage Consultation", "Precision Cut Appointment", "Deep Conditioning Treatment", "Other"];
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-lg bg-card/70 p-8 text-center">
        <p className="font-default text-2xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="full_name" rules={{
      required: "Please share your name"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm tracking-wide text-foreground">
              Your Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" className="h-11 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormDescription>First and last name</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "An email helps us reach you",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm tracking-wide text-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" className="h-11 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm tracking-wide text-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" className="h-11 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormDescription>
              Optional &ndash; we can call you if you prefer
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please choose an option"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm tracking-wide text-foreground">
              What are you reaching out about?
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-11 w-full border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-background">
                {inquiryOptions.map((option, i) => <SelectItem key={i} data-index={i} value={option}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Share whatever feels right"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm tracking-wide text-foreground">
              Your Message
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Share your hair goals or questions..." className="min-h-32 border-b border-border bg-transparent focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormDescription>
              Feel free to share any details you&rsquo;re comfortable with.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-destructive/30 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full bg-primary px-8 text-primary-foreground hover:bg-primary/90 sm:w-auto">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send with Care"}
      </Button>
    </Form>;
}
export default function SecureYourChair() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true
  });
  return <section data-section-id="2019" id="secure-your-chair" className="bg-background py-20 sm:py-28 text-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={`grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs uppercase tracking-widest text-primary">
              Connect with Velvet Shears
            </p>
            <h2 className="mt-5 font-default text-4xl font-medium leading-tight text-foreground sm:text-5xl">
              Begin your transformation
            </h2>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              We invite you to reach out for a personalized consultation. Whether you are looking for a precision cut, a vibrant balayage, or a restorative treatment, our team is here to guide you.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Share your vision with us, and we will help you achieve the look you desire with professional care.
            </p>

            <div className="mt-10 space-y-2 text-sm text-muted-foreground">
              <p>hello@velvetshears.com</p>
              <p>(555) 246-8101</p>
              <p>Tuesday to Saturday, 10am to 7pm</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="appointment_form_book" formId="appointment_form_book" className="bg-card p-8 shadow-lg sm:p-10" sectionName="secure-your-chair" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Send with Care" successMessage="Thank you for reaching out. We have received your message and will respond with care soon." errorMessage="Something went gently wrong. Please try sending your message again.">
              <ReachOutForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}